// src/App.js
import React, { useState, useEffect, useContext } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link as RouterLink,
} from 'react-router-dom';
import {
  Box,
  CssBaseline,
  AppBar,
  Toolbar,
  Button,
  Drawer,
  useTheme,
  useMediaQuery,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Container,
  Typography,
} from '@mui/material';
import axios from 'axios';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { AuthContext } from './contexts/AuthContext';
import Navbar from './components/Layout/Navbar';
import MobileNav from './components/Layout/MobileNav';
import Footer from './components/Layout/Footer';
import Home from './components/Home/Home';
import Login from './components/Auth/Login';
import Register from './components/Auth/Register';
import Dashboard from './components/Dashboard/Dashboard';
import Creators from './components/Creators/Creators';
import Leaderboard from './components/Leaderboard/Leaderboard';
import Reviews from './components/Reviews/Reviews';
import CreatorPage from './components/Creators/CreatorPage';
import SubscriptionPage from './components/Stripe/SubscriptionPage';
import PaymentPage from './components/Stripe/PaymentPage';
import SuccessPage from './components/Stripe/SuccessPage';
import TermsOfService from './components/Legal/TermsOfService';
import PrivacyPolicy from './components/Legal/PrivacyPolicy';
import API from './components/config';

export default function App() {
  const { authData } = useContext(AuthContext);
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const isSm = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Router>
      <CssBaseline />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          overflowX: 'hidden',
          bgcolor: theme.palette.background.default,
        }}
      >
        {/* Use Navbar component for all cases */}
        <Navbar />

        {/* Main content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            pt: { xs: 7, sm: 8, md: 9 }, // toolbar offset
            pb: authData && isSm ? 9 : 2, // space for mobile nav
            width: '100%',
            maxWidth: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Container 
            maxWidth="lg" 
            sx={{ 
              width: '100%',
              px: { xs: 2, sm: 3, md: 4 },
            }}
          >
            <Routes>
              <Route
                path="/"
                element={authData ? <Dashboard /> : <Home />}
              />
              <Route path="/home" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/creators" element={<Creators />} />
              <Route path="/reviews" element={<Reviews />} />
              <Route path="/leaderboard" element={<Leaderboard />} />
              <Route path="/creator/:id" element={<CreatorPage />} />
              <Route
                path="/subscription"
                element={<SubscriptionPage />}
              />
              <Route
                path="/payment"
                element={<PaymentPage />}
              />
              <Route
                path="/success"
                element={<SuccessPage />}
              />
              <Route
                path="/terms"
                element={<TermsOfService />}
              />
              <Route
                path="/privacy"
                element={<PrivacyPolicy />}
              />
            </Routes>
          </Container>
        </Box>

        {/* Mobile bottom nav */}
        {authData && isSm && (
          <MobileNav />
        )}

        {/* Footer */}
        <Box sx={{ mt: 'auto' }}>
          <Footer />
        </Box>

        <ToastContainer 
          position="top-center" 
          autoClose={3000} 
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </Box>
    </Router>
  );
}
